var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { observer } from "mobx-react-lite";
import React, { useEffect, useCallback, useState, useRef, useMemo, } from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useFormContext } from "./Form";
import { withFormWrapper, DEFAULT_ID } from "./withFormWrapper";
import { toJS } from "mobx";
import debounce from "debounce-promise";
import { bootstrapStyle } from "./Select";
import { StaticTypeahead } from "./StaticInput";
import { useTranslation } from "./hooks/useTranslation";
import { components as defaultComponents, } from "react-select";
var DEBOUNCE_TIME = 500;
var defaultNewOptionValidator = function (inputValue, selectValue, selectOptions) {
    if (!inputValue)
        return false;
    var lowerCaseInputValue = inputValue.toLowerCase();
    var currentSelection = selectValue.map(function (value) {
        return value.name.toLowerCase();
    });
    var selectableOptions = selectOptions.map(function (option) {
        return option.name.toLowerCase();
    });
    return (!currentSelection.includes(lowerCaseInputValue) &&
        !selectableOptions.includes(lowerCaseInputValue));
};
var createValueContainer = function (maxHeight) {
    var ValueContainer = function (props) {
        return (React.createElement("div", { style: {
                flex: 1,
                alignItems: "center",
                display: "flex",
                width: "100%",
                flexWrap: "wrap",
                maxHeight: maxHeight,
                overflowY: "auto",
                padding: "1px 10px",
            } }, props.children));
    };
    return ValueContainer;
};
var createMultiValueContainer = function (maxDisplayedSelectedValues, totalValues, onClickMoreValues) {
    var MultiValueContainer = function (props) {
        var _a;
        var index = (_a = props.index) !== null && _a !== void 0 ? _a : 0;
        if (index < maxDisplayedSelectedValues) {
            return React.createElement(defaultComponents.MultiValue, __assign({}, props));
        }
        if (index === maxDisplayedSelectedValues) {
            var remainingCount = totalValues - maxDisplayedSelectedValues;
            return (React.createElement("div", { className: "select__multi-value", style: {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "2px",
                    display: "flex",
                    margin: "2px",
                    minWidth: "0",
                    boxSizing: "border-box",
                    cursor: onClickMoreValues ? "pointer" : "default",
                }, onMouseDown: function (e) { return e.preventDefault(); }, onClick: onClickMoreValues },
                React.createElement("div", { className: "select__multi-value__label", role: "button", style: {
                        borderRadius: "2px",
                        color: "#333",
                        fontSize: "85%",
                        overflow: "hidden",
                        padding: "3px",
                        paddingLeft: "6px",
                        paddingRight: "6px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                    } },
                    "+",
                    remainingCount,
                    " More")));
        }
        return null;
    };
    return MultiValueContainer;
};
var RawTypeahead = observer(function (_a) {
    var name = _a.name, onChange = _a.onChange, _b = _a.multiple, multiple = _b === void 0 ? false : _b, loadOptions = _a.loadOptions, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.formatter, formatter = _d === void 0 ? function (option) { return option.label; } : _d, _e = _a.autoFocus, autoFocus = _e === void 0 ? false : _e, placeholder = _a.placeholder, _f = _a.uniqueId, uniqueId = _f === void 0 ? DEFAULT_ID : _f, _g = _a.staticFormatter, staticFormatter = _g === void 0 ? function (option) { return option.label; } : _g, label = _a.label, _h = _a.allowNew, allowNew = _h === void 0 ? false : _h, newOptionLabelFormatter = _a.newOptionLabelFormatter, newOptionFormatter = _a.newOptionFormatter, _j = _a.newOptionValidator, newOptionValidator = _j === void 0 ? allowNew ? defaultNewOptionValidator : undefined : _j, noOptionsMessage = _a.noOptionsMessage, _k = _a.isClearable, isClearable = _k === void 0 ? true : _k, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId, _l = _a.forceAlwaysLive, forceAlwaysLive = _l === void 0 ? false : _l, components = _a.components, portal = _a.portal, maxDisplayedSelectedValues = _a.maxDisplayedSelectedValues, onClickMoreValues = _a.onClickMoreValues, maxHeight = _a.maxHeight;
    var _m = useFormContext(), values = _m.values, formDisabled = _m.formDisabled, isStatic = _m.isStatic;
    var mounted = useRef(false);
    var _o = useState([]), options = _o[0], setOptions = _o[1];
    var emptyValue = multiple ? [] : null;
    var t = useTranslation();
    if (placeholder === undefined) {
        placeholder = "".concat(t("Select"), "...");
    }
    var currentValue = toJS(values.get(name)) || [];
    var valueLength = Array.isArray(currentValue) ? currentValue.length : 0;
    var customComponents = useMemo(function () {
        if (!maxDisplayedSelectedValues || !multiple) {
            return components;
        }
        return __assign(__assign({}, components), { ValueContainer: (components === null || components === void 0 ? void 0 : components.ValueContainer) ||
                (maxHeight
                    ? createValueContainer(maxHeight)
                    : defaultComponents === null || defaultComponents === void 0 ? void 0 : defaultComponents.ValueContainer), MultiValue: (components === null || components === void 0 ? void 0 : components.MultiValue) ||
                createMultiValueContainer(maxDisplayedSelectedValues, valueLength, onClickMoreValues) });
    }, [
        maxDisplayedSelectedValues,
        multiple,
        components,
        valueLength,
        onClickMoreValues,
        maxHeight,
    ]);
    var handleLoadOptions = useCallback(function (inputValue) {
        if (!mounted.current) {
            return Promise.resolve([]);
        }
        return new Promise(function (resolve) {
            loadOptions(inputValue).then(function (options) {
                if (mounted.current) {
                    setOptions(options);
                }
                resolve(options);
            });
        });
    }, [loadOptions, setOptions]);
    // Track mounted state across the component lifetime only, not across
    // effect re-runs caused by dependency changes.
    useEffect(function () {
        mounted.current = true;
        return function () {
            mounted.current = false;
        };
    }, []);
    // Debounced reloads (typing, menu close, blur) fire up to DEBOUNCE_TIME
    // after they were triggered. By then the parent may have rendered a new
    // loadOptions closure carrying fresh data, so dispatch through the latest
    // one: a pending timer must not overwrite newer options with the stale
    // results its original closure would produce.
    var handleLoadOptionsRef = useRef(handleLoadOptions);
    useEffect(function () {
        handleLoadOptionsRef.current = handleLoadOptions;
    }, [handleLoadOptions]);
    var debouncedLoadOptions = useMemo(function () {
        return debounce(function (inputValue) { return handleLoadOptionsRef.current(inputValue); }, DEBOUNCE_TIME);
    }, []);
    var handleChange = React.useCallback(function (selected) {
        if (selected === null) {
            handleLoadOptions("");
        }
        var changedValue = selected
            ? multiple
                ? selected.flat()
                : selected
            : emptyValue;
        if (onChange) {
            onChange(changedValue, values.get(name));
        }
        values.set(name, changedValue);
    }, [onChange, values, emptyValue, name, handleLoadOptions]);
    useEffect(function () {
        handleLoadOptions("");
    }, [handleLoadOptions]);
    if (isStatic && !forceAlwaysLive) {
        return (React.createElement(StaticTypeahead, { name: name, label: label, staticFormatter: staticFormatter, multiple: multiple, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
    }
    var SelectComponent = allowNew ? AsyncCreatableSelect : AsyncSelect;
    var creatableSelectProps = {
        getNewOptionData: newOptionFormatter,
        formatCreateLabel: newOptionLabelFormatter,
        isValidNewOption: newOptionValidator,
        createOptionPosition: "first",
    };
    return (React.createElement(SelectComponent, __assign({ "aria-labelledby": "form-group-".concat(name, "-").concat(uniqueId), id: "form-group-".concat(name, "-").concat(uniqueId), styles: bootstrapStyle, components: customComponents, isMulti: multiple, value: toJS(values.get(name)), onChange: handleChange, isClearable: isClearable, defaultOptions: options, onInputChange: debouncedLoadOptions, loadOptions: debouncedLoadOptions, getOptionValue: JSON.stringify, getOptionLabel: formatter, noOptionsMessage: function () { return noOptionsMessage || "".concat(t("Type to Search"), "..."); }, className: "react-select ag-custom-component-popup", isDisabled: disabled || formDisabled, autoFocus: autoFocus, menuPortalTarget: portal, classNamePrefix: "select", placeholder: placeholder }, (allowNew ? creatableSelectProps : undefined), { "data-id": dataId, "data-testid": dataTestId })));
});
export var Typeahead = withFormWrapper({ WrappedComponent: RawTypeahead });
